import { Flex } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Navigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { finleyAssert } from '@finley-technologies/common';

import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { isVerificationPipelineRunId } from '~/packages/verifications/ui/VerificationTableSharedComponents';
import { VerificationWorkflow } from '~/packages/verifications/ui/VerificationWorkflow';

// Types
const RouteParamsStruct = z.object({
  artifactDocumentId: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
  creditFacilityId: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
  reportingDate: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
  fileVersion: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
});

// Fragments
const RouteBase = () => {
  const { user } = useUserAuthOrThrow();
  const params = useParams();
  const {
    artifactDocumentId,
    creditFacilityId,
    reportingDate,
    fileVersion: fileVersionString = '',
  } = RouteParamsStruct.parse(params);

  const fileVersion = Number.parseInt(fileVersionString, 10);

  const pipelineRunId = useMemo(() => {
    const pipelineRunParams = {
      artifactDocumentId,
      creditFacilityId,
      reportingDate,
      fileVersion,
    };
    finleyAssert(
      isVerificationPipelineRunId(pipelineRunParams),
      'Invalid pipeline run parameters',
    );
    return pipelineRunParams;
  }, [artifactDocumentId, creditFacilityId, reportingDate, fileVersion]);

  return (
    <VerificationWorkflow
      organizationId={user.organization.id}
      pipelineRunId={pipelineRunId}
    />
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="/funding-requests" />;
    }}
  >
    <Flex direction="column" padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}>
      <RouteBase />
    </Flex>
  </RouteErrorBoundary>
);
